import { Col } from "@/common/css";
import FilePreviewModal from "@/common/modal/FilePreviewModal";
import Button from "@/common/ui/Button";
import { DefaultErrorBoundary } from "@/common/ui/error/Error";
import { RoundLoader } from "@/common/ui/Loader";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import { InfoText } from "@/common/ui/Text";
import EmptyList from "@/components/common/EmptyList";
import AddSignatoryModal from "@/components/dashboard/setting/signatoryAndLetterHead/AddSignatoryModal";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import { useSignatoryAndLetterHeadInfo } from "@/services/common/profile.services";
import { ColumnDef } from "@tanstack/react-table";
import React, { useEffect, useState } from "react";
import RemoveSignatory from "./modal/RemoveSignatory";
import { useSelector } from "react-redux";
import { isOrgPending, loginSelector } from "@/store/features/loginSlice";
import Attention from "@/components/dashboard/setting/security/modal/Attention";
import { isHasPermission } from "@/utils/helper/permissions";
import { PERMISSIONS } from "@/utils/constant/global.constant";
import { PermissionDenied } from "@/common/ui/page/PermissionDeniedPage";
import RenderColumn from "@/components/employee/RenderColumn";
import CommonTableView from "@/components/common/table/CommonTableView";

const modalConfig: ModalConfig = {
  addSignatory: {
    component: AddSignatoryModal,
    title: "Assign signatory",
    type: "side",
  },
  updateDocument: {
    component: AddSignatoryModal,
    title: "Update authorization document",
    type: "side",
  },
  filePreview: {
    component: FilePreviewModal,
    title: "Preview",
    type: "full",
    asChild: true,
  },
  delete: {
    component: RemoveSignatory,
    title: "Remove signatory",
    type: "center",
    isQueryRequired: true,
  },
  Attention: {
    component: Attention,
    title: "Attention",
    modalClassName: "!max-w-[330px]",
  },
};

const dynamicColumnsConfig: ColumnDef<any>[] = [
  {
    accessorKey: "employee.empId",
    header: "Emp ID",
  },
  {
    accessorKey: "employee",
    header: "Emp Name",
    cell: ({ row, column }: any) => <RenderColumn type={"profile"} row={row} column={column} />,
  },
  {
    accessorKey: "employee.email",
    header: "Email",
  },
  {
    accessorKey: "employee.designation",
    header: "Designation",
  },
  {
    accessorKey: "sig_doc.name",
    header: "Seal & Signature",
  },
  // {
  //   accessorKey: "signature",
  //   header: "Signature",
  // },
  // {
  //   accessorKey: "assignedBy",
  //   header: "Assigned by",
  // },
  {
    accessorKey: "updatedTs",
    header: "Last updated on",
  },
];

interface DocumentPageProps {
  openModal: (modalKey: string, props?: any) => void;
  activeModal: string;
}

const SignatoryPage: React.FC<DocumentPageProps> = ({ openModal, activeModal }) => {
  const isOrgPendingStatus = useSelector(isOrgPending);
  const { currentUser } = useSelector(loginSelector);
  const { data, isLoading, refetch }: any = useSignatoryAndLetterHeadInfo({ key: "sig" });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const handleOpenModal = () => openModal("addSignatory");

  const handleDeleteSignatory = (modalData: any) => {
    if (data?.sig.length === 1) {
      return openModal("Attention");
    }
    openModal("delete", { data: modalData?.employee, afterCall: refetch });
  };
  useEffect(() => {
    if (!activeModal) {
      refetch();
    }
  }, [activeModal]);

  if (isLoading) return <RoundLoader />;
  if (!isHasPermission(currentUser?.perms, PERMISSIONS.SETTING["View Signatory"])) {
    return <PermissionDenied />;
  }

  return (
    <div className={`px-2 md:px-4 flex flex-col flex-1 h-full `}>
      <SideBarContentHeader heading="Authorized signatory">
        {data?.sig?.length > 0 && (
          <div className="relative  group">
            {(isOrgPendingStatus || data?.sig.length === 3) && (
              <div
                className={`absolute -bottom-8 right-0 z-20 px-2 bg-white rounded-xl border opacity-0 group-hover:opacity-100 group-hover:translate-y-0 translate-y-2 transition-all duration-300 ease-out flex items-center py-1`}
              >
                <span className="text-10 text-text-error w-fit whitespace-nowrap opacity-100 z-50">{"Maximum of 3 signatories allowed."}</span>
              </div>
            )}
            <Button variant="primary" size="small" className="items-center " onClick={handleOpenModal} disabled={isOrgPendingStatus || data?.sig.length === 3}>
              Assign signatory
            </Button>
          </div>
        )}
      </SideBarContentHeader>
      <DefaultErrorBoundary>
        {/* Table View */}
        {data?.sig.length > 0 ? (
          <CommonTableView
            className="w-full h-[calc(100%-160px)]"
            columnClassName="max-w-[160px] w-[160px] min-w-[160px]"
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            data={data.sig}
            columnsConfig={dynamicColumnsConfig}
            setSelectedRows={(rows) => console.log(rows)}
            openModal={(modalKey, props) => console.log(modalKey, props)}
            paginationConfig={{ initialPage: 1, initialPerPage: 25, perPageOptions: [10, 25, 50, 100] }}
            hidePagination
            dropdownOptions={[
              ...(isHasPermission(currentUser?.perms, PERMISSIONS.SETTING["Edit Signatory"])
                ? [
                    {
                      label: "Update document",
                      onClickFun: (data: any) =>
                        openModal("updateDocument", {
                          data: { employeeId: data?.employee, authorizedDoc: { previewUrl: data?.sig_doc?.path, ...data?.sig_doc } },
                        }),
                    },
                  ]
                : []),
              {
                label: "Preview document",
                onClickFun: (data) => openModal("filePreview", { file: { name: data?.sig_doc?.name, url: data?.sig_doc?.path } }),
              },
              ...(isHasPermission(currentUser?.perms, PERMISSIONS.SETTING["Delete Signatory"])
                ? [
                    {
                      label: "Remove signatory",
                      onClickFun: (data: any) => handleDeleteSignatory(data),
                      type: "remove",
                    },
                  ]
                : []),
            ]}
            linkColumns={[{ id: "sig_doc_name", url: ":identifier", identifier: "sig_doc.path", isExternal: true }]}
          />
        ) : (
          <>
            {isOrgPendingStatus && <InfoText text="You can’t add signatories until your document is verified and employees are added" className="mt-4" />}{" "}
            <Col className=" justify-center items-center">
              <EmptyList
                text=""
                description="Add authorized employees to assign signatories once verification is complete"
                icon="/icons/signatory_empty_doc.svg"
                className="h-full gap-y-4 max-w-[350px] mt-32"
                textClassName="!font-semibold"
              >
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleOpenModal}
                  disabled={isOrgPendingStatus || !isHasPermission(currentUser?.perms, PERMISSIONS.SETTING["Create Signatory"])}
                >
                  Assign signatory
                </Button>
              </EmptyList>
            </Col>
          </>
        )}
      </DefaultErrorBoundary>
    </div>
  );
};

export const SignatoryPageWithModal = withModal(SignatoryPage, modalConfig);
